// The manage page reached by a signed booking link (/{slug}/manage/:token).
// Shows the booking and lets the customer cancel or reschedule. Cancel and
// reschedule are authorized by the signed token only (no login); the slug scopes
// the booking-types + availability lookups the reschedule picker needs to the
// booking's host (the public routes are per-slug, ADR-0002 section 4).
// Implements: ADR-0002 sections 4, 5; architecture.md v1.0 section 5.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { ApiError } from '../api/types.js';
import type { BookingType, PublicBooking, Slot } from '../api/types.js';
import { api } from '../api/client.js';
import {
  detectTimeZone,
  formatLongDate,
  formatTime,
  formatTimeRange,
  rollingWindow,
  shiftInstant,
  zoneLabel,
} from '../lib/time.js';
import { bookingPathLink, managePathLink, navigate } from '../lib/router.js';
import { BrandShell } from '../components/BrandShell.js';
import { SlotPicker } from '../components/SlotPicker.js';
import { Banner, Spinner, StateBlock } from '../components/feedback.js';

type View = 'loading' | 'error' | 'view' | 'reschedule' | 'cancelled' | 'rescheduled';
type Load = 'loading' | 'ready' | 'error';

const WINDOW_DAYS = 30;

export function ManagePage({ slug, token }: { slug: string; token: string }): JSX.Element {
  const [tz, setTz] = useState<string>(() => detectTimeZone());
  const [view, setView] = useState<View>('loading');
  const [booking, setBooking] = useState<PublicBooking | null>(null);
  const [loadError, setLoadError] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Cancel is irreversible (it deletes the calendar events), so gate it behind a
  // deliberate second action rather than a single click on an emailed link.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // Reschedule is likewise a deliberate two-step: picking a slot only stages it;
  // the request fires only on an explicit confirm, mirroring the cancel prompt.
  const [pendingSlot, setPendingSlot] = useState<Slot | null>(null);

  // Reschedule slot picking
  const [type, setType] = useState<BookingType | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoad, setSlotsLoad] = useState<Load>('loading');
  const [slotsError, setSlotsError] = useState<string>();
  // The reschedule PREVIEW shift: the front buffer of the LIVE selected booking
  // type, used ONLY by the reschedule preview UI (the SlotPicker labels and the
  // "Reschedule to ..." confirm prompt), because the new booking will carry the
  // buffer live at that moment. The CURRENT-booking summary and the post-reschedule
  // banner do NOT use this: they shift by the booking RECORD's own frontBufferMin
  // (see the render below), so a historical booking never drifts when the type's
  // buffer is later edited. The submitted reschedule value stays nominal (slot.start).
  const [previewFrontBufferMin, setPreviewFrontBufferMin] = useState(0);

  const load = useCallback(async () => {
    setView('loading');
    setLoadError(undefined);
    try {
      const res = await api.getBooking(token);
      setBooking(res.booking);
      // The applied front buffer is captured on the booking RECORD at creation and
      // travels on the projection (res.booking.frontBufferMin), so the current-
      // booking summary reads the ACTUAL meeting start straight from the record, with
      // no separate type lookup a private (UNLISTED) type would miss and no drift when
      // the type's buffer is later edited. The render below sources the shift from it.
      setView('view');
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 401
          ? 'This link is invalid or has expired.'
          : err instanceof ApiError && err.status === 404
            ? 'We could not find this booking.'
            : 'We could not load this booking.',
      );
      setView('error');
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadSlots = useCallback(
    async (bookingTypeId: string, zone: string) => {
      setSlotsLoad('loading');
      setSlotsError(undefined);
      const { from, to } = rollingWindow(WINDOW_DAYS);
      try {
        const res = await api.getAvailability(slug, { type: bookingTypeId, from, to, tz: zone });
        setSlots(res.slots);
        setSlotsLoad('ready');
      } catch (err) {
        setSlotsError(err instanceof ApiError ? err.message : 'Could not load availability.');
        setSlotsLoad('error');
      }
    },
    [slug],
  );

  // Resolve the booking type (for name + duration in the reschedule picker).
  async function startReschedule(): Promise<void> {
    if (!booking) return;
    setActionError(undefined);
    setPendingSlot(null);
    let resolved: BookingType | null = null;
    try {
      const res = await api.listBookingTypes(slug);
      resolved = res.bookingTypes.find((t) => t.id === booking.bookingTypeId) ?? null;
    } catch {
      resolved = null;
    }
    // Fall back to a minimal synthetic type if the lookup failed OR the type is
    // UNLISTED (the public booking-types list omits it, so `resolved` is null). The
    // picker still renders; duration is informational in the picker header. Carry
    // the booking's front buffer onto the fallback so the picker labels and the
    // confirm prompt stay shifted for a private type just as they do for a listed one.
    const fallback: BookingType = {
      id: booking.bookingTypeId,
      name: 'Your meeting',
      durationMin: Math.round(
        (Date.parse(booking.end) - Date.parse(booking.start)) / 60000,
      ),
      bufferBeforeMin: 0,
      bufferAfterMin: 0,
      ...(booking.frontBufferMin ? { frontBufferMin: booking.frontBufferMin } : {}),
    };
    const bt = resolved ?? fallback;
    setType(bt);
    // The reschedule PREVIEW shift. For a LISTED type bt is the live resolved type,
    // so the preview uses the CURRENT live buffer (the new booking will get whatever
    // is live now). For an UNLISTED type the public list omits it, so bt is the
    // synthetic fallback carrying the booking RECORD's own buffer: the best estimate
    // the client has, since the live unlisted buffer is unknowable here. Either way
    // the submitted reschedule value stays nominal (slot.start).
    setPreviewFrontBufferMin(bt.frontBufferMin ?? 0);
    setView('reschedule');
    void loadSlots(bt.id, tz);
  }

  async function doCancel(): Promise<void> {
    setBusy(true);
    setActionError(undefined);
    try {
      const res = await api.cancelBooking(token);
      setBooking(res.booking);
      setView('cancelled');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not cancel this booking.');
    } finally {
      setBusy(false);
    }
  }

  async function doReschedule(slot: Slot): Promise<void> {
    setBusy(true);
    setActionError(undefined);
    try {
      const res = await api.rescheduleBooking(token, slot.start);
      setBooking(res.booking);
      // The reschedule mints a fresh signed token; point the URL at the new one
      // (still scoped to this host's slug) so a refresh keeps working.
      navigate(managePathLink(slug, res.manageToken));
      setView('rescheduled');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
        setSlotsError('That time is no longer available. Please choose another.');
        if (type) void loadSlots(type.id, tz);
      } else {
        setActionError(err instanceof ApiError ? err.message : 'Could not reschedule.');
      }
    } finally {
      setBusy(false);
    }
  }

  const statusPill = useMemo(() => {
    if (!booking) return null;
    return booking.status === 'cancelled' ? (
      <span className="pill pill-cancelled">Cancelled</span>
    ) : (
      <span className="pill pill-confirmed">Confirmed</span>
    );
  }, [booking]);

  // Announce view transitions (view -> reschedule -> rescheduled/cancelled) so a
  // screen reader is told the content changed, instead of focus silently falling
  // back to <body>.
  const viewAnnouncement =
    view === 'reschedule'
      ? 'Choose a new time for your booking'
      : view === 'cancelled'
        ? 'Cancellation complete'
        : view === 'rescheduled'
          ? 'Reschedule complete'
          : '';

  return (
    <BrandShell
      lede="Manage your booking."
      sub="Reschedule or cancel any time using your secure link."
    >
      <p className="sr-only" role="status" aria-live="polite">
        {viewAnnouncement}
      </p>
      <header className="page-head">
        <span className="eyebrow">Your booking</span>
        <h1 className="page-title">Manage your meeting</h1>
      </header>

      {view === 'loading' ? (
        <div className="state">
          <Spinner />
          <p>Loading your booking…</p>
        </div>
      ) : null}

      {view === 'error' ? (
        <StateBlock
          title="Link not available"
          action={
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate(bookingPathLink(slug))}
            >
              Go to booking page
            </button>
          }
        >
          {loadError}
        </StateBlock>
      ) : null}

      {(view === 'view' || view === 'cancelled' || view === 'rescheduled') && booking ? (
        <div className="confirm rise" style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: 'var(--space-4)' }}>{statusPill}</div>

          {view === 'rescheduled' ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="ok">Your booking was moved. A new invite is on its way.</Banner>
            </div>
          ) : null}
          {view === 'cancelled' ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="ok">Your booking has been cancelled.</Banner>
            </div>
          ) : null}
          {actionError ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="error">{actionError}</Banner>
            </div>
          ) : null}

          {/* Show the ACTUAL meeting start (nominal booking.start + the booking
              RECORD's own front buffer); the end stays nominal. Display only,
              booking.start on the server record is untouched. Sourcing the shift
              from the record (not the live type) keeps this summary, and the
              post-reschedule banner that reuses it, pinned to what was actually
              booked (the response record, set via setBooking(res.booking)). */}
          <div className="confirm-when" style={{ marginLeft: 0 }}>
            <div className="cw-time">
              {formatTimeRange(
                shiftInstant(booking.start, booking.frontBufferMin ?? 0),
                booking.end,
                tz,
              )}
            </div>
            <div className="cw-date">
              {formatLongDate(shiftInstant(booking.start, booking.frontBufferMin ?? 0), tz)}
            </div>
            <div className="cw-tz">
              {zoneLabel(tz, shiftInstant(booking.start, booking.frontBufferMin ?? 0))}
            </div>
          </div>

          <p className="page-desc" style={{ marginTop: 'var(--space-5)' }}>
            Booked for <strong>{booking.customer.name}</strong> ({booking.customer.email}).
          </p>

          {booking.status === 'confirmed' && view === 'view' ? (
            confirmingCancel ? (
              <div className="actions">
                <p className="page-desc" style={{ width: '100%', margin: 0 }}>
                  Cancel this booking? This deletes the meeting from both calendars
                  and cannot be undone.
                </p>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={busy}
                >
                  Keep booking
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void doCancel()}
                  disabled={busy}
                >
                  {busy ? 'Cancelling…' : 'Yes, cancel'}
                </button>
              </div>
            ) : (
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-teal"
                  onClick={() => void startReschedule()}
                  disabled={busy}
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setConfirmingCancel(true)}
                  disabled={busy}
                >
                  Cancel booking
                </button>
              </div>
            )
          ) : (
            <div className="actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(bookingPathLink(slug))}
              >
                Book a new meeting
              </button>
            </div>
          )}
        </div>
      ) : null}

      {view === 'reschedule' && type ? (
        <>
          <button
            type="button"
            className="back-link"
            onClick={() => {
              setPendingSlot(null);
              setView('view');
            }}
          >
            ← Back to booking
          </button>
          {actionError ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="error">{actionError}</Banner>
            </div>
          ) : null}
          {pendingSlot ? (
            <div className="actions" style={{ marginBottom: 'var(--space-4)' }}>
              <p className="page-desc" style={{ width: '100%', margin: 0 }}>
                {/* The shifted actual start (the LIVE selected type's buffer, since
                    the new booking gets the current buffer); the submitted value
                    stays nominal. */}
                Reschedule to{' '}
                {formatLongDate(shiftInstant(pendingSlot.start, previewFrontBufferMin), tz)} at{' '}
                {formatTime(shiftInstant(pendingSlot.start, previewFrontBufferMin), tz)}?
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPendingSlot(null)}
                disabled={busy}
              >
                Keep current time
              </button>
              <button
                type="button"
                className="btn btn-teal"
                onClick={() => {
                  if (pendingSlot) void doReschedule(pendingSlot);
                }}
                disabled={busy}
              >
                {busy ? 'Rescheduling…' : 'Confirm reschedule'}
              </button>
            </div>
          ) : null}
          <SlotPicker
            bookingType={type}
            slots={slots}
            tz={tz}
            onTzChange={setTz}
            status={slotsLoad}
            errorMessage={slotsError}
            busy={busy}
            onSelect={(s) => setPendingSlot(s)}
            onRetry={() => void loadSlots(type.id, tz)}
          />
        </>
      ) : null}
    </BrandShell>
  );
}
